import { cannabinoidsMeta } from '@wysiwyg/moss-runtime';
import type { Template, TextElement } from '../wysiwyg/types';
import type { RawDataContext } from '../wysiwyg/textFormatter';
import { normalizeCoaKeys } from '../wysiwyg/coaUtils';
import { parseLabMeasurement, selectLabPercent } from './labMeasurement';
import { manualCompoundField, manualCompoundFieldValue, manualOverrideState } from './manualLabOverrides';
import { hasMatchingColoradoPotencyMapping } from './coloradoPotencyMapping';
import type { MarketRecord, PrintRunRecord } from '../types';

export const TAC_PERCENT_KEY = 'coa.ingredients.totalActiveCannabinoids.percent';
export const isTacPercentKey = (key: string | undefined): boolean =>
  typeof key === 'string' && /(?:^|\.)totalActiveCannabinoids\.percent$/.test(key);
export type TacAdjustment = {
  excluded?: string[];
  added?: Record<string, number>;
  edited?: number;
  recalculate?: boolean;
};
export type TacResolution = {
  provenance: 'reported' | 'calculated' | 'edited' | 'unavailable';
  value: number | null;
  calculated: number | null;
  adjusted: boolean;
  breakdown: Array<{ key: string; label: string; factor: number; percent: number | null; contribution: number | null; reason?: string }>;
};
export type TacPrintAudit = TacResolution & {
  summary?: TacPrintSummary;
  packageKey?: string | null;
  packageId: string | null;
  packageLabel: string | null;
  acknowledged: boolean;
  printedValues: string[];
  printedValueKind?: 'percent' | 'text';
  printedValuesProvisional?: boolean;
  outputs?: Array<TacResolution & { elementId: string; values: string[] }>;
};
export type TacPrintSummary = { calculated: number; unavailable: number };

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

export function withTacMarketPolicy<T extends Record<string, unknown>>(
  coa: T, markets: readonly MarketRecord[], marketCode?: string,
): T & { _tacMarketCalculationEnabled: boolean } {
  const code = String(marketCode || coa.marketCode || coa.stateCode || '').trim().toUpperCase();
  const market = code ? markets.find(candidate => candidate.code?.trim().toUpperCase() === code) : undefined;
  return { ...coa, _tacMarketCalculationEnabled: market?.tacCalculationEnabled === true };
}

export const tacMarketAllowsCalculation = (coa: unknown): boolean =>
  record(coa)._tacMarketCalculationEnabled === true;

export function resolveMarketTac(coa: unknown, adjustment?: TacAdjustment, calculationEnabled = true): TacResolution {
  const marketEnabled = tacMarketAllowsCalculation(coa);
  const enabled = calculationEnabled && marketEnabled;
  const resolution = resolveTac(coa, enabled ? adjustment : undefined, enabled);
  return !marketEnabled && resolution.provenance !== 'reported'
    ? { ...resolution, value: null, provenance: 'unavailable' } : resolution;
}
const canonical = (key: string) => key === 'thc' ? 'delta9thc' : key === 'thca' ? 'delta9thca' : key;
const legacyPercentage = (value: unknown, manuallyOwned = false): number | undefined => {
  if (manuallyOwned) return selectLabPercent(value);
  const measurement = record(value);
  return parseLabMeasurement(measurement.percent) ?? parseLabMeasurement(record(measurement.weight).percent);
};
export const tacConstituents = Object.entries(cannabinoidsMeta)
  .filter(([key, meta]) => !meta.isDerivative && key !== 'thc' && key !== 'thca')
  .map(([key, meta]) => ({ key, label: meta.noGreekLabel ?? meta.label, factor: key.endsWith('a') ? 0.877 : 1 }));

export function tacPackageKey(input: unknown): string | null {
  const coa = record(input);
  return typeof coa._tacPackageKey === 'string' ? coa._tacPackageKey : null;
}

export function isStagedTacPackageKey(packageKey: string | null): boolean {
  if (packageKey === null) return false;
  let identity: unknown;
  try {
    identity = JSON.parse(packageKey);
  } catch {
    throw new Error('The TAC package identity is invalid.');
  }
  if (!Array.isArray(identity) || identity.length !== 4
    || !['active', 'staged'].includes(identity[2])) {
    throw new Error('The TAC package identity is invalid.');
  }
  return identity[2] === 'staged';
}

export function withTacPackageIdentity<T extends Record<string, unknown>>(
  coa: T,
  organizationId: number | string | null | undefined,
  facilityId: number | string | null | undefined,
  packageId: number | string | null | undefined,
  stagedCoaId?: number | string | null,
): T {
  const id = stagedCoaId ?? packageId;
  if (organizationId == null || id == null || !String(id).trim()) {
    if (!('_tacPackageKey' in coa)) return coa;
    return { ...coa, _tacPackageKey: null };
  }
  return { ...coa, _tacPackageKey: JSON.stringify([String(organizationId), facilityId == null ? null : String(facilityId),
    stagedCoaId != null ? 'staged' : 'active', String(id)]) };
}

function positiveIdentityId(value: unknown): number | null {
  if ((typeof value !== 'number' && typeof value !== 'string')
    || (typeof value === 'string' && !/^\d+$/.test(value))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export function restoreSavedTacContext(
  coa: Record<string, unknown>,
  template: Pick<Template, 'tacPrintAudit'>,
  printRun: PrintRunRecord & { facilityId?: unknown },
  organizationId: number,
): Record<string, unknown> {
  const audit = template.tacPrintAudit;
  if (!audit || !['calculated', 'edited'].includes(audit.provenance)) return coa;
  if (!Number.isFinite(audit.value) || audit.acknowledged !== true || typeof audit.packageKey !== 'string') {
    throw new Error('The saved TAC audit is incomplete for this print run.');
  }

  let identity: unknown;
  try {
    identity = JSON.parse(audit.packageKey);
  } catch {
    throw new Error('The saved TAC audit package identity is invalid.');
  }
  if (!Array.isArray(identity) || identity.length !== 4
    || identity[0] !== String(organizationId)
    || (identity[1] !== null && typeof identity[1] !== 'string')
    || !['active', 'staged'].includes(identity[2])) {
    throw new Error('The saved TAC audit does not match this print run.');
  }
  const active = printRun.packageId != null;
  const stagedIdentity = identity[2] === 'staged';
  const stagedIdentityValues = [printRun.stagePackageCoaId, printRun.stagedPrintContext?.stagedCoaId]
    .filter((value): value is string | number => value != null);
  const stagedIdentityIds = stagedIdentityValues.map(positiveIdentityId);
  const stagedIdentityInvalid = stagedIdentityIds.some(id => id === null)
    || new Set(stagedIdentityIds).size > 1;
  const stagedId = stagedIdentityIds[0] ?? null;
  const expectedId = stagedIdentity
    ? active ? stagedId : stagedId ?? positiveIdentityId(coa.coaId)
    : active ? positiveIdentityId(printRun.packageId) : null;
  if (!Number.isSafeInteger(organizationId) || organizationId <= 0 || expectedId === null
    || positiveIdentityId(identity[3]) !== expectedId
    || (stagedIdentity && (stagedIdentityInvalid || (active && stagedIdentityValues.length === 0)
      || audit.packageId != null))
    || (!stagedIdentity && (!active
      || (audit.packageId != null && positiveIdentityId(audit.packageId) !== expectedId)))) {
    throw new Error('The saved TAC audit does not match this print run.');
  }

  const facilityIds = [
    printRun.facilityId,
    printRun.stagedPrintContext?.facilityId,
    coa.facilityId ?? coa.FacilityId,
  ].filter((value): value is string | number => typeof value === 'string' || typeof value === 'number').map(String);
  if (facilityIds.length && facilityIds.some(id => id !== identity[1])) {
    throw new Error('The saved TAC audit does not match this print run.');
  }
  const expectedLabel = [printRun.packageLabel, coa.packageTag]
    .find((value): value is string => typeof value === 'string' && Boolean(value.trim()));
  if (typeof audit.packageLabel === 'string' && expectedLabel
    && audit.packageLabel.trim().toLowerCase() !== expectedLabel.trim().toLowerCase()) {
    throw new Error('The saved TAC audit does not match this print run.');
  }

  return { ...coa, _tacMarketCalculationEnabled: true, _tacPackageKey: audit.packageKey };
}

export function getTacAdjustment(template: Pick<Template, 'tacAdjustments'> | null, coa: unknown): TacAdjustment | undefined {
  const key = tacPackageKey(coa);
  return key && template?.tacAdjustments && Object.prototype.hasOwnProperty.call(template.tacAdjustments, key)
    ? template.tacAdjustments[key] : undefined;
}

// Only an owned print snapshot may transfer an edit to its replacement staged row.
export function rebindStagedTacAdjustment(template: Template, organizationId: number | string, stagedCoaId: number | string | null | undefined): Template {
  const previousKey = template.tacPrintAudit?.packageKey;
  if (!previousKey || stagedCoaId == null) return template;
  const identity: unknown = JSON.parse(previousKey);
  if (!Array.isArray(identity) || identity[0] !== String(organizationId) || identity[2] !== 'staged') return template;
  const adjustment = template.tacAdjustments?.[previousKey];
  if (!adjustment) return template;
  const nextKey = JSON.stringify([identity[0], identity[1], 'staged', String(stagedCoaId)]);
  return { ...template, tacAdjustments: { ...template.tacAdjustments, [nextKey]: adjustment },
    tacPrintAudit: { ...template.tacPrintAudit!, packageKey: nextKey } };
}

function reportedPercentages(coa: Record<string, unknown>, overrides: Record<string, unknown>): Record<string, unknown> {
  const ingredients = record(normalizeCoaKeys(coa).ingredients);
  if (record(coa.resultSetProvenance).policyVersion !== 2) {
    const result = Object.fromEntries(Object.entries(ingredients).map(([key, value]) => {
      const canonicalKey = canonical(key);
      const manuallyOwned = manualOverrideState(overrides, `lab_cb_${canonicalKey}_override`) === true;
      return [canonicalKey, legacyPercentage(value, manuallyOwned)];
    }));
    // Canonical measurements take precedence over their historical aliases, including null.
    for (const key of ['delta9thc', 'delta9thca']) {
      if (key in ingredients) {
        const manuallyOwned = manualOverrideState(overrides, `lab_cb_${key}_override`) === true;
        result[key] = legacyPercentage(ingredients[key], manuallyOwned);
      }
    }
    return result;
  }

  const result: Record<string, unknown> = {};
  const isColorado = String(coa.stateCode ?? '').trim().toUpperCase() === 'CO';
  const groups = Array.isArray(coa.analyteResults) ? coa.analyteResults : [];
  for (const group of groups) {
    const measurements = record(group).measurements;
    for (const value of Array.isArray(measurements) ? measurements : [group]) {
      const measurement = record(value);
      if (measurement.unit !== '%') continue;
      const identity = record(measurement.provenance).analyteKey;
      if (typeof identity !== 'string') continue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(identity);
      } catch (error) {
        if (!(error instanceof SyntaxError)) throw error;
        continue; // Unresolvable identities remain explicitly missing in the breakdown.
      }
      if (isColorado && !hasMatchingColoradoPotencyMapping(measurement, parsed)) continue;
      if (Array.isArray(parsed) && parsed[0] === 'cannabinoid' && typeof parsed[1] === 'string') {
        const key = canonical(parsed[1]);
        if (key === parsed[1] || !(key in result)) result[key] = measurement.value;
      }
    }
  }
  return result;
}

function manualTacOverrides(coa: Record<string, unknown>): Record<string, unknown> {
  let value = coa._labManualOverrides ?? coa.manualOverrides ?? coa._labOverrides;
  while (typeof value === 'string') value = JSON.parse(value);
  if (value == null) return {};
  if (typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Stored TAC overrides must be an object.');
  }
  const overrides = record(value);
  const totalOverrideKey = 'lab_cb_totalactivecannabinoids_override';
  const totalFamily = manualCompoundField(totalOverrideKey)?.family;
  for (const [key, enabled] of Object.entries(overrides)) {
    const field = manualCompoundField(key);
    if (field?.family === totalFamily && field.suffix === 'override'
      && enabled !== undefined && typeof enabled !== 'boolean') {
      throw new Error('A stored TAC override control must be boolean.');
    }
  }
  return overrides;
}

export function resolveTac(input: unknown, adjustment: TacAdjustment = {}, calculationEnabled = true): TacResolution {
  const coa = record(input);
  const overrides = manualTacOverrides(coa);
  const reported = reportedPercentages(coa, overrides);
  const breakdown = tacConstituents.map(({ key, label, factor }) => {
    const added = Object.prototype.hasOwnProperty.call(adjustment.added ?? {}, key);
    const percent = parseLabMeasurement(added ? adjustment.added?.[key] : reported[key]) ?? null;
    const reason = adjustment.excluded?.includes(key) ? 'Removed by user'
      : percent === null ? 'No reported percentage' : undefined;
    return { key, label, factor, percent, contribution: reason ? null : percent! * factor, ...(reason ? { reason } : {}) };
  });
  const included = breakdown.filter(row => row.contribution !== null);
  const hasReportedConstituent = tacConstituents.some(row => parseLabMeasurement(reported[row.key]) !== undefined);
  const calculated = hasReportedConstituent && included.length
    ? included.reduce((total, row) => total + row.contribution!, 0) : null;
  const direct = parseLabMeasurement(adjustment.edited);
  const totalOverrideKey = 'lab_cb_totalactivecannabinoids_override';
  const manuallyOwned = manualOverrideState(overrides, totalOverrideKey) === true;
  const recalculate = adjustment.recalculate === true || adjustment.excluded !== undefined || adjustment.added !== undefined;
  const inheritedEdit = manuallyOwned && !recalculate;
  const inheritedPercent = manualCompoundFieldValue(overrides, totalOverrideKey, 'percent');
  const inheritedPercentCleared = inheritedPercent === null
    || (typeof inheritedPercent === 'string' && inheritedPercent.trim() === '');
  const inheritedValue = inheritedEdit
    ? parseLabMeasurement(inheritedPercent)
      ?? (inheritedPercentCleared ? undefined
        : legacyPercentage(record(coa.ingredients).totalActiveCannabinoids, true))
    : undefined;
  // An owned ingredients total is not lab evidence, including after explicit reversion.
  const metrc = manuallyOwned ? undefined : parseLabMeasurement(reported.totalActiveCannabinoids);
  const fallback = calculationEnabled ? calculated : null;
  const value = direct ?? (inheritedEdit ? inheritedValue ?? null : metrc ?? fallback);
  return {
    value,
    calculated,
    provenance: direct !== undefined || inheritedEdit ? 'edited'
      : metrc !== undefined ? 'reported' : fallback !== null ? 'calculated' : 'unavailable',
    adjusted: Boolean(adjustment.excluded?.length || Object.keys(adjustment.added ?? {}).length),
    breakdown,
  };
}

export function elementUsesTac(element: TextElement): boolean {
  const containsTac = (text?: string) => Boolean(text && [...text.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)]
    .some(([, key]) => isTacPercentKey(key.trim())));
  return isTacPercentKey(element.variableKey) || containsTac(element.text) ||
    Boolean(element.conditions?.some(condition => isTacPercentKey(condition.variableKey) || containsTac(condition.text)));
}

export function elementHasTacContext(element: TextElement): boolean {
  return element.tacCalculationEnabled === true || elementUsesTac(element);
}

type TacTemplate = Pick<Template, 'tacAdjustments' | 'templateFn' | 'labelFn'> & { elements?: Template['elements'] };

export function templateAllowsTacCalculation(template: TacTemplate | null | undefined): boolean {
  return Boolean(template?.elements?.some(element => element.type === 'text' && element.tacCalculationEnabled === true));
}

export function resolveTemplateTac(coa: unknown, template: TacTemplate | null | undefined): TacResolution {
  const enabled = templateAllowsTacCalculation(template);
  return resolveMarketTac(coa, enabled ? getTacAdjustment(template ?? null, coa) : undefined, enabled);
}

export function tacElementContext<T extends Record<string, unknown>>(
  element: TextElement,
  data: T,
  rawDataContext?: RawDataContext,
  themeData?: Record<string, unknown>,
) {
  const coa = element.tacCalculationEnabled === true ? rawDataContext?.coa : rawDataContext?.tacReportedCoa;
  const reportedFunctionData = element.tacCalculationEnabled === true ? undefined : rawDataContext?.tacReportedFunctionData;
  const usesTac = elementHasTacContext(element);
  if ((!usesTac && !reportedFunctionData) || !coa) return { data, rawDataContext };
  const effectiveData: Record<string, unknown> = { ...data, ...reportedFunctionData };
  // Per-label renderers apply theme values after template functions.
  for (const key of [...Object.keys(themeData ?? {}), 'qrValue', 'qrIndex', 'labelIndex']) {
    if (Object.prototype.hasOwnProperty.call(data, key)) effectiveData[key] = data[key];
  }
  const ingredients = record(coa.ingredients);
  const value = record(ingredients.totalActiveCannabinoids).percent;
  const projectedIngredients = {
    ...record(effectiveData.ingredients),
    totalActiveCannabinoids: { ...record(record(effectiveData.ingredients).totalActiveCannabinoids), percent: value },
  };
  return {
    data: { ...effectiveData, ...(usesTac ? { coa, ingredients: projectedIngredients,
      [TAC_PERCENT_KEY]: value, 'ingredients.totalActiveCannabinoids.percent': value } : {}) },
    rawDataContext: { ...rawDataContext, coa },
  };
}

export function templateUsesTac(template: TacTemplate | null | undefined): boolean {
  return Boolean(template?.elements?.some(element => element.type === 'text' && elementHasTacContext(element)) ||
    template?.templateFn?.includes('totalActiveCannabinoids') || template?.labelFn?.includes('totalActiveCannabinoids'));
}

/** A render-local projection: never write calculated values back to the canonical COA. */
export function withResolvedTac<T extends Record<string, unknown>>(coa: T, template: TacTemplate): T {
  if (!templateUsesTac(template)) return coa;
  const resolution = resolveTemplateTac(coa, template);
  const ingredients = record(coa.ingredients);
  return {
    ...coa,
    ingredients: {
      ...ingredients,
      totalActiveCannabinoids: { ...record(ingredients.totalActiveCannabinoids), percent: resolution.value ?? undefined },
    },
  };
}

export function withTacSources(template: Template, coa: unknown): Template {
  return {
    ...template,
    elements: template.elements.map(element => {
      if (element.type !== 'text' || !elementHasTacContext(element)) return element;
      const enabled = element.tacCalculationEnabled === true;
      const { provenance } = resolveMarketTac(coa, enabled ? getTacAdjustment(template, coa) : undefined, enabled);
      return { ...element, source: provenance === 'reported' ? 'metrc' as const : provenance === 'edited' ? 'edited' as const
        : provenance === 'calculated' ? 'calculated' as const : undefined };
    }),
  };
}
